import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Call as TwilioCall } from '@twilio/voice-sdk';
import type { CallTransport, IncomingCallPayload } from '../lib/websocket/events';

/**
 * Active call state. The shape varies by transport:
 *
 *   webrtc — the staff browser owns the RTCPeerConnection and the local +
 *   remote MediaStreams. `pc` and `localStream` are required; `remoteStream`
 *   is set asynchronously when the first track arrives.
 *
 *   twilio — the Twilio Voice SDK owns the audio plumbing. We hold the
 *   `Call` object so we can `mute()`, `disconnect()`, etc. There's no
 *   `pc`/`localStream`/`remoteStream` to thread into the UI; the SDK pipes
 *   audio through its own internal sink.
 */
export interface ActiveCallState {
  call: IncomingCallPayload;
  transport: CallTransport;
  pc?: RTCPeerConnection;
  remoteStream?: MediaStream | null;
  localStream?: MediaStream | null;
  twilioCall?: TwilioCall;
  answeredAt: string;
}

interface VoiceState {
  /** Currently ringing inbound call (if any). */
  incomingCall: IncomingCallPayload | null;
  /** Active call this staff member answered. */
  activeCall: ActiveCallState | null;
  /** Whether this user wants to receive incoming-call toasts. Persisted. */
  availableForCalls: boolean;

  setIncoming: (call: IncomingCallPayload | null) => void;
  clearIncoming: () => void;
  setActive: (active: ActiveCallState | null) => void;
  clearActive: () => void;
  toggleAvailable: () => void;
  setAvailable: (value: boolean) => void;
}

export const useVoiceStore = create<VoiceState>()(
  persist(
    (set) => ({
      incomingCall: null,
      activeCall: null,
      availableForCalls: true,

      setIncoming: (call) => set({ incomingCall: call }),
      clearIncoming: () => set({ incomingCall: null }),
      setActive: (active) => set({ activeCall: active }),
      clearActive: () => set({ activeCall: null }),
      toggleAvailable: () =>
        set((state) => ({ availableForCalls: !state.availableForCalls })),
      setAvailable: (value) => set({ availableForCalls: value }),
    }),
    {
      name: 'voice-storage',
      // Only persist the user preference. Live call state must reset on reload.
      partialize: (state) => ({ availableForCalls: state.availableForCalls }),
    }
  )
);
